import { lireJson, type Resultat } from './client';

/** Une ligne du journal : qui a fait quoi, quand. */
export interface EntreeJournal {
  readonly id: number;
  readonly quand: string;
  readonly compteId: string | null;
  readonly email: string;
  readonly action: string;
  readonly actionLibelle: string;
  readonly cible: string;
  readonly details: string;
}

export interface PageJournal {
  readonly items: readonly EntreeJournal[];
  readonly total: number;
  readonly page: number;
  readonly taille: number;
  readonly pages: number;
}

/** Les actions tracées, avec leur libellé — même liste que le serveur. */
export const ACTIONS: ReadonlyArray<{ readonly code: string; readonly libelle: string }> = [
  { code: 'compte.cree', libelle: 'Compte créé' },
  { code: 'compte.invite', libelle: 'Invitation créée' },
  { code: 'invitation.revoquee', libelle: 'Invitation révoquée' },
  { code: 'compte.role', libelle: 'Rôle modifié' },
  { code: 'compte.suspendu', libelle: 'Compte suspendu' },
  { code: 'compte.reactive', libelle: 'Compte réactivé' },
  { code: 'compte.archive', libelle: 'Compte archivé' },
  { code: 'compte.restaure', libelle: 'Compte restauré' },
  { code: 'comptes.purges', libelle: 'Archives purgées' },
  { code: 'invitation.renvoyee', libelle: 'Invitation renvoyée' },
  { code: 'client.cree', libelle: 'Client créé' },
  { code: 'client.modifie', libelle: 'Client modifié' },
  { code: 'intervention.creee', libelle: 'Intervention créée' },
  { code: 'intervention.modifiee', libelle: 'Intervention modifiée' },
  { code: 'interventions.statuts', libelle: 'Statut d’interventions modifié' },
  { code: 'clients.archives', libelle: 'Clients archivés' },
  { code: 'rapport.cree', libelle: 'Compte rendu ouvert' },
  { code: 'rapport.modifie', libelle: 'Compte rendu enregistré' },
  { code: 'rapport.soumis', libelle: 'Compte rendu transmis' },
];

export function urlJournal(
  criteres: { page?: number; taille?: number; action?: string; cible?: string } = {},
): string {
  const parametres = new URLSearchParams();
  if (criteres.action) parametres.set('action', criteres.action);
  // La cible isole l'historique d'UN compte : le serveur compare sans la casse.
  if (criteres.cible) parametres.set('cible', criteres.cible);
  if (criteres.page && criteres.page > 1) parametres.set('page', String(criteres.page));
  if (criteres.taille) parametres.set('taille', String(criteres.taille));
  const requete = parametres.toString();
  return requete === '' ? '/api/admin/journal' : `/api/admin/journal?${requete}`;
}

/**
 * Le journal du COMPTE connecté (tout rôle) : ses traces, filtrables et paginées.
 *
 * Distinct de `lireJournal` (administrateur, journal global).
 */
export interface PageMonJournal extends PageJournal {
  /** Les actions filtrables, servies par le serveur. */
  readonly actions: ReadonlyArray<{ readonly code: string; readonly libelle: string }>;
  /**
   * `true` seulement pour un administrateur : le journal GLOBAL lui est ouvert.
   * Le prestataire n'a pas à voir un lien qui mène à « écran réservé ».
   */
  readonly journalComplet: boolean;
}

export function urlMonJournal(
  criteres: { page?: number; taille?: number; action?: string } = {},
): string {
  const parametres = new URLSearchParams();
  if (criteres.action) parametres.set('action', criteres.action);
  if (criteres.page && criteres.page > 1) parametres.set('page', String(criteres.page));
  if (criteres.taille) parametres.set('taille', String(criteres.taille));
  const requete = parametres.toString();
  return requete === '' ? '/api/journal' : `/api/journal?${requete}`;
}

export function lireMonJournal(
  criteres: { page?: number; taille?: number; action?: string } = {},
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<PageMonJournal>> {
  return lireJson<PageMonJournal>(urlMonJournal(criteres), options);
}

export function lireJournal(
  criteres: { page?: number; taille?: number; action?: string; cible?: string } = {},
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<PageJournal>> {
  return lireJson<PageJournal>(urlJournal(criteres), options);
}
